import { animated } from '@react-spring/web'
import { useCallback, useContext, useMemo } from 'react'

import { useBlinkingState } from '../../lib/preview-animation-hook'
import { type StepDefinitionProps } from '../../lib/step-definition'
import { FrameBuilderDefaultFrameContext } from '../../contexts/default-frame-context'
import { FrameBuilderSelectionContext } from '../../contexts/selection-context'
import { FrameBuilderSelectedStateContext } from '../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../contexts/validation-context'

import Button from '../utility/button'
import ColorInput from '../utility/color-input'
import PortableText from '../utility/portable-text'
import ConfigurationDefinition from './configuration-definition'

const ArtworkImageDefinition = ({
  number,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { selectedStateSelectionMessages } = useContext(
    FrameBuilderSelectionContext,
  )
  const { validationMessages } = useContext(FrameBuilderValidationContext)
  const { defaultState } = useContext(FrameBuilderDefaultFrameContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const [imageOpacity, setIsImageBlinking] = useBlinkingState()

  // TODO: Wrong way to check for valid state!
  const isValidSelection = useMemo(
    () => !validationMessages?.artworkImage,
    [validationMessages],
  )

  const updateImage = useCallback(
    (url: string, name: string) => {
      dispatchSelectedState({
        type: 'artworkImage',
        url,
        name,
      })
    },
    [dispatchSelectedState],
  )
  const updateBackgroundColor = useCallback(
    (color: string) => {
      dispatchSelectedState({
        type: 'artworkImage',
        color,
      })
    },
    [dispatchSelectedState],
  )
  const updateFile = useCallback(
    (files: FileList | null) => {
      let file: File | null = null
      const fileCount = files?.length ?? 0

      for (let i = 0; i < fileCount; i += 1) {
        if (files?.[i].type.match(/^image\//)) {
          file = files[i]
          break
        }
      }

      if (file !== null) {
        const fileUrl = URL.createObjectURL(file)
        updateImage(fileUrl, file.name)
      }
    },
    [updateImage],
  )

  const artworkImageMessage =
    validationMessages?.artworkImage ??
    selectedStateSelectionMessages?.artworkImage

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsImageBlinking(true)
    }
  }, [isValidSelection, setIsImageBlinking, onClose])

  return (
    <ConfigurationDefinition
      number={number}
      onClose={onClose}
      title={frameBuilderStrings.artworkPreviewStepTitle}
      className={className}
    >
      <div className="flex flex-col px-6 space-y-6">
        <animated.div
          style={{
            opacity: imageOpacity,
          }}
        >
          {frameBuilderStrings.artworkPreviewArtworkLabel && (
            <span>{frameBuilderStrings.artworkPreviewArtworkLabel}</span>
          )}
          {frameBuilderStrings.artworkPreviewArtworkDescription && (
            <div>
              <PortableText
                content={frameBuilderStrings.artworkPreviewArtworkDescription}
              />
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(event) => updateFile(event.target.files)}
            className="w-full mt-4 border-0 text-xs bg-white"
          />
        </animated.div>

        <div className="flex flex-col">
          {frameBuilderStrings.artworkPreviewBackgroundColorLabel && (
            <span>
              {frameBuilderStrings.artworkPreviewBackgroundColorLabel}
            </span>
          )}

          <ColorInput
            defaultValue={
              selectedState.artwork.backgroundColor?.current ?? 'white'
            }
            placeholder={
              frameBuilderStrings.artworkPreviewBackgroundColorPlaceholder
            }
            predefinedColors={
              defaultState.artwork.backgroundColor?.predefinedColors ?? []
            }
            onChange={updateBackgroundColor}
            className="mt-4"
          />
        </div>

        <p className="uppercase text-gray-500 leading-5 break-all">
          {artworkImageMessage}
        </p>

        <div className="flex justify-between mt-6">
          <Button
            size="small"
            isVisuallyDisabled={!isValidSelection}
            onClick={handleOkClick}
            className="w-[49%] grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>

      {frameBuilderStrings.artworkPreviewStepFooter && (
        <>
          <hr className="bg-gray-300" />
          <div className="flex flex-col px-6 space-y-6">
            <PortableText
              content={frameBuilderStrings.artworkPreviewStepFooter}
            />
          </div>
        </>
      )}
    </ConfigurationDefinition>
  )
}

export default ArtworkImageDefinition
